import { Badge, Center, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from "@chakra-ui/react"
import { FeatureToggleContainer } from "container/service/FeatureToggleContainer"
import { ActiveLimitOrdersContainer } from "container/service/LimitOrderServiceContainer/useActiveLimitOrder"
import { useTickerSymbolFromRouterParams } from "hook/useRouterParams"
import { SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { usePositionTaker } from "sdk-react/clearingHouse/usePositionTaker"
import { scrollbarStyle } from "theme/scrollbarStyle"

import { ActiveLimitOrderList } from "./ActiveOrderList"
import { FilledOrderList } from "./FilledOrderList"
import { PositionDetail } from "./PositionDetail"

interface OrderCountBadgeProps {
    isHighlighted: boolean
    amount: number
}

function OrderCountBadge({ isHighlighted, amount }: OrderCountBadgeProps) {
    if (amount !== 0) {
        return (
            <Badge
                ml={2}
                color={isHighlighted ? "whiteAlpha.800" : "blackAlpha.800"}
                colorScheme={isHighlighted ? "blackAlpha" : "perpetual"}
                variant={"solid"}
                size={"sm"}
            >
                {amount}
            </Badge>
        )
    } else {
        return null
    }
}

export function TradingInfo() {
    const { t } = useTranslation()
    const { isLimitOrderEnable } = FeatureToggleContainer.useContainer()
    const tickerSymbol = useTickerSymbolFromRouterParams()
    const position = usePositionTaker(tickerSymbol)
    const hasPosition = !!position
    const [tabIndex, setTabIndex] = useState(0)

    const { activeLimitOrdersMap, isCreating } = ActiveLimitOrdersContainer.useContainer()
    const orders = activeLimitOrdersMap[tickerSymbol] ?? []
    const orderAmount = orders.length

    useEffect(() => {
        if (isLimitOrderEnable && isCreating) {
            setTabIndex(1)
        }
    }, [isCreating, isLimitOrderEnable])

    return (
        <Tabs
            isLazy
            lazyBehavior="keepMounted"
            index={tabIndex}
            w="100%"
            h="100%"
            variant="soft-rounded"
            colorScheme="gray"
            size={"sm"}
            onChange={(index: SetStateAction<number>) => setTabIndex(index)}
        >
            <TabList px={4} pb={2} pr="48px" overflowX={"auto"} maxW="100%" whiteSpace="nowrap">
                <Tab>{t("general.position")}</Tab>

                {isLimitOrderEnable && (
                    <Tab>
                        {t("general.limit_orders")}
                        <OrderCountBadge amount={orderAmount} isHighlighted={tabIndex === 1} />
                    </Tab>
                )}

                <Tab>{t("general.fills")}</Tab>
            </TabList>

            <TabPanels w="100%" h="100%" overflow={"hidden"}>
                {/* position tab */}
                <TabPanel px={0} pt={5} pb={0} w="100%" h="calc(100% - 20px)" sx={scrollbarStyle} overflow={"auto"}>
                    {hasPosition && position ? (
                        <PositionDetail position={position} />
                    ) : (
                        <Center h="200px">
                            <VStack w="300px" textAlign="center">
                                <Text color="whiteAlpha.500" fontSize="md">
                                    You don't have any positions yet.
                                </Text>
                            </VStack>
                        </Center>
                    )}
                </TabPanel>

                {/* limit orders tab */}
                {isLimitOrderEnable && (
                    <TabPanel px={0} pt={5} pb={0} w="100%" h="calc(100% - 20px)" sx={scrollbarStyle} overflow={"auto"}>
                        <ActiveLimitOrderList />
                    </TabPanel>
                )}

                {/* fills tab */}
                <TabPanel px={0} pt={5} pb={0} w="100%" h="calc(100% - 20px)" sx={scrollbarStyle} overflow={"auto"}>
                    <FilledOrderList tickerSymbol={tickerSymbol} />
                </TabPanel>
            </TabPanels>
        </Tabs>
    )
}
